import React, { useState, useEffect } from 'react';
import { pricingService, sitesService, emailSettingsService, getErrorMessage } from '../services/api';
import {
  NativeButton,
  NativeCard,
  NativeCardHeader,
  NativeCardTitle,
  NativeCardDescription,
  NativeCardContent,
  NativeInput,
  NativeSelect,
  NativeAlert
} from '../components/NativeComponents';
import { 
  Loader2, 
  AlertCircle,
  RefreshCw,
  Settings,
  DollarSign,
  Save,
  Mail,
  Server,
  TestTube,
  Eye,
  EyeOff
} from 'lucide-react';
import { toast } from 'sonner';

export default function SettingsPage() {
  const [pricing, setPricing] = useState(null);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [pricePerKwh, setPricePerKwh] = useState('');
  const [selectedSiteId, setSelectedSiteId] = useState('');

  const [emailSettings, setEmailSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    smtp_use_tls: true,
    sender_email: '',
    sender_name: 'HIMS Şarj Sistemi'
  });
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailTesting, setEmailTesting] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [pricingData, sitesData, emailData] = await Promise.all([
        pricingService.get(),
        sitesService.list(),
        emailSettingsService.get().catch(() => null)
      ]);
      setPricing(pricingData);
      setSites(sitesData || []);
      
      if (pricingData?.price_per_kwh) {
        setPricePerKwh(pricingData.price_per_kwh.toString());
      }

      if (emailData) {
        setEmailSettings(prev => ({
          ...prev,
          smtp_host: emailData.smtp_host || '',
          smtp_port: emailData.smtp_port || 587,
          smtp_user: emailData.smtp_user || '',
          smtp_use_tls: emailData.smtp_use_tls !== false,
          sender_email: emailData.sender_email || '',
          sender_name: emailData.sender_name || 'HIMS Şarj Sistemi'
        }));
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSavePricing = async (e) => {
    e.preventDefault();
    
    const price = parseFloat(pricePerKwh);
    if (!price || price <= 0) {
      toast.error('Gecerli bir fiyat girin');
      return;
    }

    setSaving(true);
    try {
      await pricingService.update(price, selectedSiteId || null);
      toast.success('Fiyat basariyla guncellendi');
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEmailSettings = async (e) => {
    e.preventDefault();
    
    if (!emailSettings.smtp_host || !emailSettings.smtp_user) {
      toast.error('SMTP sunucu ve kullanıcı adı zorunludur');
      return;
    }

    setEmailSaving(true);
    try {
      await emailSettingsService.update(emailSettings);
      toast.success('Email ayarlari kaydedildi');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setEmailSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmailAddress || !testEmailAddress.includes('@')) {
      toast.error('Gecerli bir test email adresi girin');
      return;
    }

    setEmailTesting(true);
    try {
      await emailSettingsService.test(testEmailAddress);
      toast.success(`Test emaili gonderildi: ${testEmailAddress}`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setEmailTesting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '256px' }}>
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <NativeAlert variant="danger" icon={AlertCircle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{error}</span>
          <NativeButton variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw style={{ width: '16px', height: '16px' }} />
            Tekrar Dene
          </NativeButton>
        </div>
      </NativeAlert>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }} data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ margin: 0 }}>Ayarlar</h1>
        <p className="text-slate-500 dark:text-slate-400" style={{ margin: '4px 0 0' }}>Sistem ayarlarını yönetin</p>
      </div>

      {/* Pricing Settings */}
      <NativeCard>
        <NativeCardHeader>
          <NativeCardTitle icon={DollarSign}>Fiyatlandirma</NativeCardTitle>
          <NativeCardDescription>kWh başına şarj fiyatını ayarlayın</NativeCardDescription>
        </NativeCardHeader>
        <NativeCardContent>
          <form onSubmit={handleSavePricing}>
            {pricing && (
              <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', marginBottom: '16px' }} className="dark:bg-slate-800">
                <p className="text-slate-500 dark:text-slate-400" style={{ fontSize: '14px', margin: 0 }}>Mevcut Fiyat</p>
                <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#16a34a', margin: '4px 0 0' }}>
                  {pricing.price_per_kwh?.toFixed(2)} TL/kWh
                </p>
                {pricing.updated_at && (
                  <p className="text-slate-500 dark:text-slate-400" style={{ fontSize: '12px', marginTop: '4px' }}>
                    Son guncelleme: {new Date(pricing.updated_at).toLocaleString('tr-TR')}
                  </p>
                )}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
              <NativeInput
                label="Yeni Fiyat (TL/kWh)"
                id="price_per_kwh"
                type="number"
                step="0.01"
                min="0.01"
                value={pricePerKwh}
                onChange={(e) => setPricePerKwh(e.target.value)}
                placeholder="5.00"
                required
              />

              <NativeSelect
                label="Site (Opsiyonel)"
                id="site_id"
                value={selectedSiteId || "all"}
                onChange={(e) => setSelectedSiteId(e.target.value === "all" ? "" : e.target.value)}
                options={[{ value: 'all', label: 'Tum Siteler' }, ...sites.map((site) => ({ value: site.id, label: site.name }))]}
              />
            </div>

            <NativeButton type="submit" disabled={saving} style={{ marginTop: '16px' }}>
              {saving ? (
                <>
                  <Loader2 className="animate-spin" style={{ width: '16px', height: '16px' }} />
                  Kaydediliyor...
                </>
              ) : (
                <>
                  <Save style={{ width: '16px', height: '16px' }} />
                  Fiyati Kaydet
                </>
              )}
            </NativeButton>
          </form>
        </NativeCardContent>
      </NativeCard>

      {/* System Info */}
      <NativeCard>
        <NativeCardHeader>
          <NativeCardTitle icon={Settings}>Sistem Bilgileri</NativeCardTitle>
        </NativeCardHeader>
        <NativeCardContent>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
            <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px' }} className="dark:bg-slate-800">
              <p className="text-slate-500 dark:text-slate-400" style={{ fontSize: '14px', margin: 0 }}>Toplam Site</p>
              <p className="text-slate-900 dark:text-white" style={{ fontSize: '20px', fontWeight: 'bold', margin: '4px 0 0' }}>{sites.length}</p>
            </div>
            <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px' }} className="dark:bg-slate-800">
              <p className="text-slate-500 dark:text-slate-400" style={{ fontSize: '14px', margin: 0 }}>Sistem Versiyonu</p>
              <p className="text-slate-900 dark:text-white" style={{ fontSize: '20px', fontWeight: 'bold', margin: '4px 0 0' }}>HIMS v1.0</p>
            </div>
          </div>
        </NativeCardContent>
      </NativeCard>

      {/* Email Settings */}
      <NativeCard>
        <NativeCardHeader>
          <NativeCardTitle icon={Mail}>Email Ayarlari (SMTP)</NativeCardTitle>
          <NativeCardDescription>Ekstre ve bildirim emaillerinin gonderilmesi icin SMTP ayarlarini yapilandirin</NativeCardDescription>
        </NativeCardHeader>
        <NativeCardContent>
          <form onSubmit={handleSaveEmailSettings}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
              <div style={{ position: 'relative' }}>
                <Server style={{ position: 'absolute', left: '12px', top: '38px', width: '16px', height: '16px' }} className="text-slate-400" />
                <NativeInput
                  label="SMTP Sunucu"
                  id="smtp_host"
                  value={emailSettings.smtp_host}
                  onChange={(e) => setEmailSettings({ ...emailSettings, smtp_host: e.target.value })}
                  placeholder="smtp.gmail.com"
                  style={{ paddingLeft: '40px' }}
                  data-testid="smtp-host-input"
                />
              </div>

              <NativeInput
                label="Port"
                id="smtp_port"
                type="number"
                value={emailSettings.smtp_port}
                onChange={(e) => setEmailSettings({ ...emailSettings, smtp_port: parseInt(e.target.value) || 587 })}
                placeholder="587"
                data-testid="smtp-port-input"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
              <NativeInput
                label="Kullanıcı Adı (Email)"
                id="smtp_user"
                type="email"
                value={emailSettings.smtp_user}
                onChange={(e) => setEmailSettings({ ...emailSettings, smtp_user: e.target.value })}
                placeholder="noreply@sirket.com"
                data-testid="smtp-user-input"
              />

              <div style={{ position: 'relative' }}>
                <NativeInput
                  label="Sifre / App Password"
                  id="smtp_password"
                  type={showPassword ? "text" : "password"}
                  value={emailSettings.smtp_password}
                  onChange={(e) => setEmailSettings({ ...emailSettings, smtp_password: e.target.value })}
                  placeholder="••••••••"
                  data-testid="smtp-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ position: 'absolute', right: '12px', top: '38px', background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                  className="text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff style={{ width: '16px', height: '16px' }} /> : <Eye style={{ width: '16px', height: '16px' }} />}
                </button>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
              <NativeInput
                label="Gonderen Email"
                id="sender_email"
                type="email"
                value={emailSettings.sender_email}
                onChange={(e) => setEmailSettings({ ...emailSettings, sender_email: e.target.value })}
                placeholder="Boş bırakılırsa kullanıcı adı kullanılır"
                data-testid="sender-email-input"
              />

              <NativeInput
                label="Gonderen Adi"
                id="sender_name"
                value={emailSettings.sender_name}
                onChange={(e) => setEmailSettings({ ...emailSettings, sender_name: e.target.value })}
                placeholder="HIMS Şarj Sistemi"
                data-testid="sender-name-input"
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <input
                type="checkbox"
                id="smtp_use_tls"
                checked={emailSettings.smtp_use_tls}
                onChange={(e) => setEmailSettings({ ...emailSettings, smtp_use_tls: e.target.checked })}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                data-testid="smtp-tls-checkbox"
              />
              <label htmlFor="smtp_use_tls" className="text-slate-700 dark:text-slate-200" style={{ fontSize: '14px', cursor: 'pointer' }}>
                TLS/STARTTLS Kullan (Onerilen)
              </label>
            </div>

            <NativeButton type="submit" disabled={emailSaving}>
              {emailSaving ? (
                <>
                  <Loader2 className="animate-spin" style={{ width: '16px', height: '16px' }} />
                  Kaydediliyor...
                </>
              ) : (
                <>
                  <Save style={{ width: '16px', height: '16px' }} />
                  Email Ayarlarini Kaydet
                </>
              )}
            </NativeButton>
          </form>

          {/* Test Email Section */}
          <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e5e7eb' }} className="dark:border-slate-700">
            <h4 className="text-slate-900 dark:text-white" style={{ fontWeight: '500', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <TestTube style={{ width: '16px', height: '16px' }} />
              Test Email Gonder
            </h4>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="email"
                value={testEmailAddress}
                onChange={(e) => setTestEmailAddress(e.target.value)}
                placeholder="test@email.com"
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  fontSize: '14px',
                  outline: 'none'
                }}
                className="dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                data-testid="test-email-input"
              />
              <NativeButton
                type="button"
                variant="outline"
                onClick={handleTestEmail}
                disabled={emailTesting || !emailSettings.smtp_host}
                data-testid="test-email-button"
              >
                {emailTesting ? (
                  <>
                    <Loader2 className="animate-spin" style={{ width: '16px', height: '16px' }} />
                    Gonderiliyor...
                  </>
                ) : (
                  <>
                    <Mail style={{ width: '16px', height: '16px' }} />
                    Test Et
                  </>
                )}
              </NativeButton>
            </div>
            <p className="text-slate-500 dark:text-slate-400" style={{ fontSize: '13px', marginTop: '8px' }}>
              Ayarlari kaydettikten sonra test emaili gonderebilirsiniz.
            </p>
          </div>
        </NativeCardContent>
      </NativeCard>
    </div>
  );
}
